import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useTopOffenders } from '@/api/hooks/useTopOffenders';
import { parsePeriod } from '@/lib/timePeriod';
import { formatAlertTime } from '@/lib/format';
import type { OffenderEntity, TopOffenderRow } from '@/types/offenders';

/**
 * Top Offenders panel — sits side-by-side with the Critical Alerts Board
 * on the Fraud Hub (1fr_320px grid on lg+).
 *
 * Shows the top 10 offenders for the selected entity + period ranked by
 * alert count (then risk-score sum, then recency). An entity selector
 * (vehicle / station / merchant / driver) makes the station / merchant /
 * driver fraud-summary marts reachable (Phase 2b — previously the panel
 * hardcoded `entity: 'vehicle'`, so those marts were dormant).
 *
 * Only vehicle rows deep-link (to `/app/vehicle/:asset_token`); the other
 * entity types have no per-entity page yet, so their rows render as plain
 * (non-navigating) list items with the same layout.
 *
 * Period-aware: reads `?period=` from the URL via useSearchParams. The
 * page-level <TimePeriodSelector defaultPeriod="D" /> seeds the URL on
 * mount so the panel almost always reads a non-null value. For period=LTD
 * the backend serves this panel from the pre-aggregated per-entity
 * fraud-summary MVs (all-time, all-status).
 *
 * `primary_location` is populated by the backend for the live per-period
 * path; the LTD/MV path returns null and the panel renders an em-dash.
 */

const _ENTITY_OPTIONS: { value: OffenderEntity; label: string; noun: string }[] = [
  { value: 'vehicle', label: 'Vehicles', noun: 'vehicles' },
  { value: 'station', label: 'Stations', noun: 'stations' },
  { value: 'merchant', label: 'Merchants', noun: 'merchants' },
  { value: 'driver', label: 'Drivers', noun: 'drivers' },
];

export function TopOffendersPanel(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');

  const [entity, setEntity] = useState<OffenderEntity>('vehicle');

  const { data, isLoading, isError, error } = useTopOffenders({
    entity,
    period,
    limit: 10,
  });

  const periodLabel = data?.period_label ?? '';
  const offenders = data?.offenders ?? [];
  const noun =
    _ENTITY_OPTIONS.find((o) => o.value === entity)?.noun ?? 'entities';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">
            Top Offenders{periodLabel ? ` — ${periodLabel}` : ''}
          </CardTitle>
          <Select
            value={entity}
            onValueChange={(v) => setEntity(v as OffenderEntity)}
          >
            <SelectTrigger
              className="h-7 w-[120px] shrink-0 text-xs"
              aria-label="Offender entity type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {_ENTITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Top 10 {noun} by alert volume. Watch hidden.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <PanelSkeleton />}

        {!isLoading && isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            Failed to load offenders: {error?.message ?? 'unknown error'}
          </div>
        )}

        {!isLoading && !isError && offenders.length === 0 && (
          <div className="flex h-[200px] flex-col items-center justify-center gap-2 px-2 text-center text-sm text-muted-foreground">
            <p className="font-medium">No offenders in this period</p>
            <p className="text-xs">
              The panel lights up automatically once new alerts land.
            </p>
          </div>
        )}

        {!isLoading && !isError && offenders.length > 0 && (
          <ol className="flex flex-col gap-1.5">
            {offenders.map((row) => (
              <OffenderRow key={row.entity_id} row={row} entity={entity} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function PanelSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function OffenderRow({
  row,
  entity,
}: {
  row: TopOffenderRow;
  entity: OffenderEntity;
}): JSX.Element {
  const body = (
    <>
      {/* Rank chip */}
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold tabular-nums text-muted-foreground">
        {row.offender_rank}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="truncate font-mono text-xs font-medium"
            title={row.entity_label}
          >
            {row.entity_label}
          </span>
          <span
            className="shrink-0 rounded-sm bg-red-100 px-1.5 py-px text-[10px] font-semibold tabular-nums text-red-900"
            title={`${row.alert_count} alerts`}
          >
            {row.alert_count}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
          <span className="truncate" title={row.primary_location ?? '—'}>
            {row.primary_location ?? '—'}
          </span>
          <span
            className="shrink-0 tabular-nums"
            title={`Risk-score sum / last alert: ${formatAlertTime(row.last_alert_at)}`}
          >
            risk {row.risk_score_sum.toLocaleString('en-ZA')}
          </span>
        </div>
      </div>
    </>
  );

  // Only vehicles have a per-entity deep-dive page today; other entity types
  // render as a plain (non-navigating) row with identical layout.
  if (entity === 'vehicle') {
    return (
      <li>
        <Link
          to={`/vehicle/${encodeURIComponent(row.entity_id)}`}
          className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 transition-colors hover:bg-accent/40"
        >
          {body}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <div className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5">
        {body}
      </div>
    </li>
  );
}

export default TopOffendersPanel;
